import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import {
    format,
    parse,
    startOfWeek,
    getDay,
    startOfMonth,
    endOfMonth,
    endOfWeek,
    startOfDay,
    endOfDay,
    isSameDay,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { fetchCalendar } from '../../../redux/actions/calendar';
import { Button } from '../../../components/buttons/Button';
import { CalendarSidebar } from './CalendarSidebar';
import { CalendarToolbar } from './CalendarToolbar';
import { CalendarEventCard } from './CalendarEventCard';
import { EventHoverPopover } from './EventHoverPopover';
import { CalendarSkeleton } from './CalendarSkeleton';
import { DrawerCourseForm } from '../DrawerCourseForm';
import { TaskDetailDrawer } from '../tasks/TaskDetailDrawer';
import { canManageCourses } from '../../../constants/courseManage';
import { useDeferredHover } from './hooks/useDeferredHover';
import { useIsMobileCalendar } from './hooks/useIsMobileCalendar';
import { getTimeViewCalendarHeight, getMonthViewCalendarHeight, getViewportCalendarMinHeight } from './mobileCalendarHeight';
import {
    EVENT_KINDS,
    applyCourseFilters,
    buildCalendarEvents,
    collectMarkedDayKeys,
    deriveLanguageOptions,
    deriveTeacherOptions,
    filterCoursesForSidebarOptions,
    filterEventsByRange,
    isCalendarColorKey,
    summarizeEvents,
    groupSidebarTasks,
} from './calendarEvents';
import { isTaskObserver } from '../../../common/calendarScope';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar.css';

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 0 }),
    getDay,
    locales: { es },
});

const MESSAGES = {
    date: 'Fecha',
    time: 'Hora',
    event: 'Evento',
    allDay: 'Todo el día',
    week: 'Semana',
    work_week: 'Semana laboral',
    day: 'Día',
    month: 'Mes',
    previous: 'Anterior',
    next: 'Siguiente',
    yesterday: 'Ayer',
    tomorrow: 'Mañana',
    today: 'Hoy',
    agenda: 'Agenda',
    noEventsInRange: 'No hay nada programado en este periodo.',
    showMore: (count) => `+${count} más`,
};

const VIEWS = [Views.MONTH, Views.WEEK, Views.DAY];

/** Rango a consultar según la vista, con margen para cubrir semanas partidas. */
const resolveRange = (date, view) => {
    if (view === Views.DAY) {
        return { from: startOfDay(date), to: endOfDay(date) };
    }
    if (view === Views.WEEK) {
        return {
            from: startOfWeek(date, { weekStartsOn: 0 }),
            to: endOfWeek(date, { weekStartsOn: 0 }),
        };
    }
    return {
        from: startOfWeek(startOfMonth(date), { weekStartsOn: 0 }),
        to: endOfWeek(endOfMonth(date), { weekStartsOn: 0 }),
    };
};

/** Encabezado de día al estilo de la referencia: día de la semana sobre el número. */
const DayHeader = ({ date }) => {
    const isToday = isSameDay(date, new Date());

    return (
        <div className="py-2 px-1 flex flex-col items-center gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {format(date, 'EEE', { locale: es })}
            </span>
            <span
                className={`flex items-center justify-center min-w-[2rem] h-8 rounded-lg text-base font-bold ${
                    isToday ? 'bg-indigo-600 text-white' : 'text-gray-900'
                }`}
            >
                {format(date, 'd')}
            </span>
        </div>
    );
};

const TimeGutterHeader = () => {
    const offset = -(new Date().getTimezoneOffset() / 60);
    const label = `GMT${offset >= 0 ? '+' : '−'}${Math.abs(offset)}`;

    return (
        <span className="block text-[10px] font-semibold text-gray-400 text-center pt-2">
            {label}
        </span>
    );
};

/**
 * @param {object} props
 * @param {object} [props.filters] Filtros compartidos de /cursos (drawer de filtros).
 * @param {() => void} [props.onCourseUpdated] Sincroniza el listado de cursos al editar.
 */
export const CoursesCalendar = ({ filters = {}, onCourseUpdated }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const {
        loading,
        loaded,
        error,
        canViewTasks,
        scope,
        courses,
        classes,
        tasks,
    } = useSelector((state) => state.calendar);

    const [date, setDate] = useState(new Date());
    const [view, setView] = useState(Views.WEEK);
    const isMobileCalendar = useIsMobileCalendar();
    const calendarView = isMobileCalendar ? Views.DAY : view;
    const calendarViews = isMobileCalendar ? [Views.DAY] : VIEWS;
    const [selectedLanguageIds, setSelectedLanguageIds] = useState([]);
    const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
    const [focusedEventId, setFocusedEventId] = useState(null);
    const [courseDrawer, setCourseDrawer] = useState(null);
    const [taskDrawer, setTaskDrawer] = useState(null);
    const gridPanelRef = useRef(null);
    const [viewportMinHeight, setViewportMinHeight] = useState(0);

    const {
        hover,
        openHover,
        keepHover,
        closeHover,
        dismissHover,
    } = useDeferredHover();

    const userCanManageCourses = canManageCourses(user?.typeUser);
    const showTeacherFilter = isTaskObserver(user);

    const drawerFilteredCourses = useMemo(
        () => applyCourseFilters(courses, filters),
        [courses, filters],
    );

    const teacherOptions = useMemo(
        () => deriveTeacherOptions(filterCoursesForSidebarOptions(drawerFilteredCourses, {
            selectedLanguageIds,
            exclude: 'teacher',
        })),
        [drawerFilteredCourses, selectedLanguageIds],
    );

    const languageOptions = useMemo(
        () => deriveLanguageOptions(filterCoursesForSidebarOptions(drawerFilteredCourses, {
            selectedTeacherIds,
            exclude: 'language',
        })),
        [drawerFilteredCourses, selectedTeacherIds],
    );

    const handleEditCourse = useCallback((courseId) => {
        dismissHover();
        setCourseDrawer({ courseId });
    }, [dismissHover]);

    const handleCloseCourseDrawer = useCallback(() => {
        setCourseDrawer(null);
    }, []);

    const handleOpenTask = useCallback((taskId, courseId) => {
        dismissHover();
        setTaskDrawer({ taskId, courseId });
    }, [dismissHover]);

    const handleCloseTaskDrawer = useCallback(() => {
        setTaskDrawer(null);
    }, []);

    const bindEventPointer = useCallback((calendarEvent) => ({
        onOpenDetail: (nativeEvent) => {
            const target = nativeEvent.currentTarget.closest('.rbc-event')
                || nativeEvent.currentTarget;
            openHover(calendarEvent, target);
        },
        onCloseDetail: closeHover,
    }), [openHover, closeHover]);

    const EventSlot = useCallback(({ event }) => {
        const pointerHandlers = bindEventPointer(event);
        const isMonthView = calendarView === Views.MONTH;

        if (isMonthView) {
            return (
                <div className="cal-event-slot">
                    <CalendarEventCard
                        event={event}
                        variant="month"
                        {...pointerHandlers}
                    />
                </div>
            );
        }

        return (
            <div className="h-full cal-event-slot--time">
                <CalendarEventCard
                    event={event}
                    onOpenDetail={pointerHandlers.onOpenDetail}
                    onCloseDetail={pointerHandlers.onCloseDetail}
                />
            </div>
        );
    }, [bindEventPointer, calendarView]);

    const range = useMemo(() => resolveRange(date, calendarView), [date, calendarView]);

    const load = useCallback(() => {
        dispatch(fetchCalendar(range));
    }, [dispatch, range]);

    const handleCourseDrawerSuccess = useCallback(() => {
        load();
        onCourseUpdated?.();
    }, [load, onCourseUpdated]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (loading) dismissHover();
    }, [loading, dismissHover]);

    // Si cambia el alcance de cursos, se descartan filtros que ya no existen.
    useEffect(() => {
        setSelectedLanguageIds((prev) => {
            const available = new Set(languageOptions.map((option) => option.id));
            const next = prev.filter((id) => available.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [languageOptions]);

    useEffect(() => {
        if (!showTeacherFilter) return;
        setSelectedTeacherIds((prev) => {
            const available = new Set(teacherOptions.map((option) => option.id));
            const next = prev.filter((id) => available.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [teacherOptions, showTeacherFilter]);

    const events = useMemo(() => buildCalendarEvents({
        courses,
        classes,
        tasks,
        canViewTasks,
        filters,
        selectedLanguageIds,
        selectedTeacherIds: showTeacherFilter ? selectedTeacherIds : [],
    }), [
        courses,
        classes,
        tasks,
        canViewTasks,
        filters,
        selectedLanguageIds,
        selectedTeacherIds,
        showTeacherFilter,
    ]);

    const hasActiveFilters = useMemo(() => (
        selectedLanguageIds.length > 0
        || (showTeacherFilter && selectedTeacherIds.length > 0)
        || Boolean(filters.identifier)
        || Boolean(filters.teacher)
        || [filters.language, filters.level, filters.status, filters.days]
            .some((value) => (value || []).length > 0)
    ), [filters, selectedLanguageIds, selectedTeacherIds, showTeacherFilter]);

    const showUpcoming = scope === 'teacher';

    const sidebarEvents = useMemo(
        () => filterEventsByRange(events, range),
        [events, range],
    );

    const summary = useMemo(() => summarizeEvents(sidebarEvents), [sidebarEvents]);
    const taskGroups = useMemo(
        () => groupSidebarTasks(sidebarEvents, { showUpcoming }),
        [sidebarEvents, showUpcoming],
    );
    const markedDayKeys = useMemo(() => collectMarkedDayKeys(events), [events]);

    // La rejilla empieza con las clases y siempre llega a las 23:59,
    // para que las entregas de fin de día quepan abajo, no en el encabezado.
    const { min, max } = useMemo(() => {
        const hours = events
            .filter((event) => event.resource?.kind === EVENT_KINDS.CLASS)
            .map((event) => event.start.getHours());

        const first = hours.length ? Math.max(Math.min(...hours) - 1, 5) : 7;

        return {
            min: new Date(1970, 0, 1, first, 0, 0),
            max: new Date(1970, 0, 1, 23, 59, 0),
        };
    }, [events]);

    const calendarHeight = useMemo(() => {
        const contentHeight = calendarView === Views.MONTH
            ? getMonthViewCalendarHeight()
            : getTimeViewCalendarHeight(min, max, { mobile: isMobileCalendar });
        return Math.max(contentHeight, viewportMinHeight || 0);
    }, [calendarView, isMobileCalendar, min, max, viewportMinHeight]);

    useLayoutEffect(() => {
        const updateViewportFloor = () => {
            const el = gridPanelRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top;
            setViewportMinHeight(getViewportCalendarMinHeight(top));
        };

        updateViewportFloor();
        window.addEventListener('resize', updateViewportFloor);
        return () => window.removeEventListener('resize', updateViewportFloor);
    }, [loading, loaded, calendarView, isMobileCalendar, error]);

    const eventPropGetter = useCallback((event) => {
        const { kind, colorKey, classSession } = event.resource || {};
        const tone = isCalendarColorKey(colorKey) ? colorKey : 'indigo';
        const classNames = [
            'cal-event',
            kind === EVENT_KINDS.TASK ? 'cal-event--task' : 'cal-event--class',
            `cal-event--${tone}`,
        ];

        if (kind === EVENT_KINDS.TASK && event.resource?.task?.windowState === 'upcoming') {
            classNames.push('cal-event--upcoming');
        }
        if (classSession?.temporalStatus === 'past') classNames.push('cal-event--past');
        if (classSession?.temporalStatus === 'live') classNames.push('cal-event--live');
        if (event.id === focusedEventId) classNames.push('cal-event--focused');

        return { className: classNames.join(' ') };
    }, [focusedEventId]);

    const focusTaskEvent = useCallback((event) => {
        if (!event?.start) return;
        setFocusedEventId(event.id);
        setDate(event.start);
        if (!isMobileCalendar && calendarView === Views.MONTH) setView(Views.WEEK);
        dismissHover();
    }, [calendarView, dismissHover, isMobileCalendar]);

    const handleCalendarViewChange = useCallback((nextView) => {
        if (isMobileCalendar) return;
        setView(nextView);
    }, [isMobileCalendar]);

    const toggleLanguage = (languageId) => {
        const id = String(languageId);
        setSelectedLanguageIds((prev) => (
            prev.map(String).includes(id)
                ? prev.filter((item) => String(item) !== id)
                : [...prev, id]
        ));
    };

    const toggleTeacher = (teacherId) => {
        const id = String(teacherId);
        setSelectedTeacherIds((prev) => (
            prev.map(String).includes(id)
                ? prev.filter((item) => String(item) !== id)
                : [...prev, id]
        ));
    };

    const focusedEvent = useMemo(
        () => events.find((event) => event.id === focusedEventId),
        [events, focusedEventId],
    );

    useEffect(() => {
        if (!focusedEventId) return undefined;
        const timer = window.setTimeout(() => {
            const el = document.querySelector('.courses-calendar .cal-event--focused');
            el?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [focusedEventId, date, calendarView]);

    const Toolbar = useCallback((props) => (
        <CalendarToolbar {...props} lockView={isMobileCalendar} />
    ), [isMobileCalendar]);

    const components = useMemo(() => ({
        toolbar: Toolbar,
        event: EventSlot,
        timeGutterHeader: TimeGutterHeader,
        week: { header: DayHeader },
        day: { header: DayHeader },
    }), [EventSlot, Toolbar]);

    const showInitialSkeleton = loading && !loaded;
    const showRefreshSkeleton = loading && loaded;

    return (
        <div className="courses-calendar w-full grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-4 items-start">
            <CalendarSidebar
                date={date}
                onDateChange={setDate}
                calendarView={calendarView}
                markedDayKeys={markedDayKeys}
                summary={summary}
                taskGroups={taskGroups}
                showUpcoming={showUpcoming}
                canViewTasks={canViewTasks}
                languageOptions={languageOptions}
                selectedLanguageIds={selectedLanguageIds}
                onToggleLanguage={toggleLanguage}
                onClearLanguages={() => setSelectedLanguageIds([])}
                showTeacherFilter={showTeacherFilter}
                teacherOptions={teacherOptions}
                selectedTeacherIds={selectedTeacherIds}
                onToggleTeacher={toggleTeacher}
                onClearTeachers={() => setSelectedTeacherIds([])}
                focusedEventId={focusedEventId}
                onSelectTaskEvent={focusTaskEvent}
            />

            <section
                ref={gridPanelRef}
                className="courses-calendar__grid-panel min-w-0 w-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-visible"
                style={viewportMinHeight ? { minHeight: viewportMinHeight } : undefined}
                aria-busy={loading}
            >
                {error ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-20 px-4 text-center">
                        <p className="text-sm text-gray-600 m-0">{error}</p>
                        <Button variant="outline" size="sm" onClick={load}>Reintentar</Button>
                    </div>
                ) : showInitialSkeleton ? (
                    <div className="p-2 sm:p-3">
                        <CalendarSkeleton view={calendarView} />
                    </div>
                ) : (
                    <div
                        className={`courses-calendar__grid-inner relative p-2 sm:p-3${
                            showRefreshSkeleton ? ' courses-calendar__grid--loading' : ''
                        }`}
                    >
                        {showRefreshSkeleton ? (
                            <CalendarSkeleton view={calendarView} overlay />
                        ) : null}

                        {loaded && !loading && events.length === 0 && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none px-4">
                                <div className="flex flex-col items-center gap-2 px-5 py-4 bg-white/95 border border-gray-200 rounded-xl text-center max-w-xs">
                                    <CalendarDaysIcon className="w-8 h-8 text-gray-300" aria-hidden="true" />
                                    <p className="text-sm font-semibold text-gray-700 m-0">
                                        Nada programado en este periodo
                                    </p>
                                    <p className="text-xs text-gray-500 m-0">
                                        {hasActiveFilters
                                            ? 'Prueba quitando algún filtro o cambia de fecha.'
                                            : isMobileCalendar
                                                ? 'Cambia de día para ver otras fechas.'
                                                : 'Cambia de semana o de mes para ver otras fechas.'}
                                    </p>
                                </div>
                            </div>
                        )}

                        <Calendar
                            localizer={localizer}
                            culture="es"
                            events={events}
                            date={date}
                            view={calendarView}
                            views={calendarViews}
                            onNavigate={setDate}
                            onView={handleCalendarViewChange}
                            onSelectEvent={(event, nativeEvent) => {
                                const isTask = event.resource?.kind === EVENT_KINDS.TASK;
                                const hasFinePointer = window.matchMedia(
                                    '(hover: hover) and (pointer: fine)',
                                ).matches;
                                if (isTask || !hasFinePointer || calendarView === Views.MONTH) {
                                    const target = nativeEvent?.currentTarget;
                                    if (target) openHover(event, target);
                                    return;
                                }
                            }}
                            selectable={false}
                            components={components}
                            eventPropGetter={eventPropGetter}
                            messages={MESSAGES}
                            min={min}
                            max={max}
                            scrollToTime={focusedEvent?.start || min}
                            step={30}
                            timeslots={2}
                            popup
                            tooltipAccessor={() => null}
                            style={{ height: calendarHeight, width: '100%' }}
                        />
                    </div>
                )}
            </section>

            {hover?.event && (
                <EventHoverPopover
                    event={hover.event}
                    anchorRect={hover.rect}
                    onClose={closeHover}
                    onKeepOpen={keepHover}
                    onDismiss={dismissHover}
                    onEditCourse={userCanManageCourses ? handleEditCourse : undefined}
                    onOpenTask={handleOpenTask}
                />
            )}

            <TaskDetailDrawer
                isOpen={Boolean(taskDrawer)}
                taskId={taskDrawer?.taskId ?? null}
                courseId={taskDrawer?.courseId ?? null}
                onClose={handleCloseTaskDrawer}
                onSubmitted={handleCourseDrawerSuccess}
            />

            <DrawerCourseForm
                isOpen={Boolean(courseDrawer)}
                courseId={courseDrawer?.courseId ?? null}
                onClose={handleCloseCourseDrawer}
                onSuccess={handleCourseDrawerSuccess}
            />
        </div>
    );
};
